import React from "react";
import {
  Map,
  TileLayer,
  Marker,
  Popup,
  Polygon,
  Rectangle,
  Polyline,
} from "react-leaflet";
import PropTypes from "prop-types";
import "leaflet/dist/leaflet.css";

import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import iconRetina from "leaflet/dist/images/marker-icon-2x.png";
import L from "leaflet";

let DefaultIcon = L.icon({
  ...L.Icon.Default.prototype.options,
  iconUrl: icon,
  iconRetinaUrl: iconRetina,
  shadowUrl: iconShadow,
});
L.Marker.prototype.options.icon = DefaultIcon;

function calculateZoom(coords) {
  if (coords.length === 0) return 5;
  if (coords.length === 1) return 8;
  let minLat = coords[0][0],
    maxLat = coords[0][0];
  let minLng = coords[0][1],
    maxLng = coords[0][1];
  coords.forEach(([lat, lng]) => {
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
  });
  const latSpan = maxLat - minLat;
  const lngSpan = maxLng - minLng;
  const maxSpan = Math.max(latSpan, lngSpan);
  if (maxSpan < 0.01) return 15;
  if (maxSpan < 0.05) return 13;
  if (maxSpan < 0.1) return 11;
  if (maxSpan < 0.5) return 9;
  if (maxSpan < 2) return 7;
  return 5;
}

export const GeoLocationMap = ({ geoLocations }) => {
  let totalLat = 0.0;
  let totalLong = 0.0;
  let pointsCounter = 0;
  let allCoords = [];

  function addCoordsValues(latitude, longitude) {
    totalLat += latitude;
    totalLong += longitude;
    pointsCounter++;
    allCoords.push([latitude, longitude]);
  }

  const renderGeoLocation = (location, index) => {
    const {
      geographicDescription,
      point,
      points,
      boundingBox,
      boundingPolygon,
      lineString,
    } = location;

    if (point) {
      addCoordsValues(point.latitude, point.longitude);
      const position = [point.latitude, point.longitude];
      return (
        <Marker key={index} position={position}>
          <Popup>{geographicDescription}</Popup>
        </Marker>
      );
    }
    if (points) {
      return points.map((p, i) => {
        addCoordsValues(p.latitude, p.longitude);
        const position = [p.latitude, p.longitude];
        return (
          <Marker key={`${index}-${i}`} position={position}>
            <Popup>{geographicDescription}</Popup>
          </Marker>
        );
      });
    }
    if (boundingBox) {
      const bounds = [
        [boundingBox.southBoundLatitude, boundingBox.westBoundLongitude],
        [boundingBox.northBoundLatitude, boundingBox.eastBoundLongitude],
      ];
      addCoordsValues(
        boundingBox.southBoundLatitude,
        boundingBox.westBoundLongitude,
      );
      addCoordsValues(
        boundingBox.northBoundLatitude,
        boundingBox.eastBoundLongitude,
      );
      return (
        <Rectangle key={index} bounds={bounds}>
          <Popup>{geographicDescription}</Popup>
        </Rectangle>
      );
    }
    if (boundingPolygon) {
      return boundingPolygon.map((poly, polyIndex) => {
        const positions = poly.points.map((p) => {
          addCoordsValues(p.latitude, p.longitude);
          return [p.latitude, p.longitude];
        });
        return (
          <Polygon key={`${index}-${polyIndex}`} positions={positions}>
            <Popup>{geographicDescription}</Popup>
          </Polygon>
        );
      });
    }
    if (lineString) {
      const positions = lineString.map((p) => {
        addCoordsValues(p.latitude, p.longitude);
        return [p.latitude, p.longitude];
      });
      return (
        <Polyline key={index} positions={positions}>
          <Popup>{geographicDescription}</Popup>
        </Polyline>
      );
    }

    return null;
  };

  const mapShapes = geoLocations
    .map((location, index) => renderGeoLocation(location, index))
    .filter((shape) => shape !== null);

  let centerPoint = [49, 12];
  if (mapShapes.length > 0) {
    centerPoint = [totalLat / pointsCounter, totalLong / pointsCounter];
  }
  const zoom = calculateZoom(allCoords);

  return (
    <>
      <Map
        center={centerPoint}
        zoom={zoom}
        scrollWheelZoom={true}
        style={{
          height: "50vh",
          width: "100%",
          border: "1px solid #ccc",
          borderRadius: "8px",
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {mapShapes.length > 0 && mapShapes}
      </Map>
    </>
  );
};

GeoLocationMap.propTypes = {
  geoLocations: PropTypes.array.isRequired,
};
